import React, { useState } from "react";
import { Box, Button, Typography, Card, CardHeader, CardContent, Paper, Grid, TextField, Container, Stack, Chip, alpha } from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import { searchProducts, addProduct } from "../../product/services/productService";
import { addPurchaseInvoice } from "../services/purchaseService";
import SupplierSelect from "../../suppliers/components/SupplierSelect";
import ProductSelect from "../../invoices/components/ProductSelect";
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

// Static suppliers
const staticSuppliers = [
  { value: 1, label: "Acme Supplies", Name: "Acme Supplies" },
  { value: 2, label: "Global Traders", Name: "Global Traders" },
  { value: 3, label: "FreshMart", Name: "FreshMart" },
];

function AddPurchasePage() {
  const navigate = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isNewProduct, setIsNewProduct] = useState(false);
  const [newProductData, setNewProductData] = useState({ Name: "", SKU: "", SalePrice: "", PurchasePrice: "" });
  const [quantity, setQuantity] = useState(1);
  const [purchasePrice, setPurchasePrice] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().slice(0, 10));
  const [currentOrder, setCurrentOrder] = useState([]);
  const [invoiceNumber, setInvoiceNumber] = useState(() => {
    const year = new Date().getFullYear();
    return `PI-${year}-${String(Date.now() % 10000).padStart(4, "0")}`;
  });

  const loadProductOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];
    let paramsObj;
    if (/^\d+$/.test(inputValue)) {
      paramsObj = { id: inputValue, sku: inputValue };
    } else {
      paramsObj = { name: inputValue, sku: inputValue };
    }
    const data = await searchProducts(paramsObj);
    const productsArray = Array.isArray(data?.payload) ? data.payload : Array.isArray(data) ? data : [];
    return productsArray.map((p) => ({
      value: p.Id,
      label: `${p.Name} (ID: ${p.Id})${p.SKU ? " - SKU: " + p.SKU : ""}`,
      ...p,
    }));
  };

  const loadSupplierOptions = async (inputValue) => {
    if (!inputValue) return staticSuppliers;
    return staticSuppliers.filter(s =>
      s.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  const handleProductSelect = (product) => {
    if (product && product.value === "new") {
      setIsNewProduct(true);
      setSelectedProduct(null);
    } else {
      setIsNewProduct(false);
      setSelectedProduct(product);
      setPurchasePrice(product?.PurchasePrice || "");
    }
  };

  const handleAddToOrder = async () => {
    let productId = selectedProduct?.Id;
    let productName = selectedProduct?.Name || newProductData.Name;
    let sku = selectedProduct?.SKU || newProductData.SKU;

    if (isNewProduct) {
      try {
        const newProduct = await addProduct(newProductData);
        productId = newProduct.Id;
        productName = newProduct.Name;
        sku = newProduct.SKU;
      } catch (error) {
        console.error('Error adding new product:', error);
        toast.error('Failed to add new product');
        return;
      }
    }

    setCurrentOrder(prev => [
      ...prev,
      {
        productId,
        productName,
        sku,
        quantity: Number(quantity),
        purchasePrice: Number(purchasePrice),
        totalPrice: Number(quantity) * Number(purchasePrice)
      }
    ]);

    // Reset product fields
    setSelectedProduct(null);
    setIsNewProduct(false);
    setNewProductData({ Name: "", SKU: "", SalePrice: "", PurchasePrice: "" });
    setQuantity(1);
    setPurchasePrice("");
  };

  const handleSubmitPurchaseOrder = async () => {
    if (!selectedSupplier || currentOrder.length === 0) {
      toast.error('Please select supplier and add at least one product');
      return;
    }

    const purchaseData = {
      InvoiceNumber: invoiceNumber,
      SupplierId: selectedSupplier.value,
      SupplierName: selectedSupplier.Name,
      PurchaseDate: purchaseDate,
      Items: currentOrder.map(item => ({
        ProductId: item.productId,
        Quantity: item.quantity,
        UnitPrice: item.purchasePrice
      }))
    };

    try {
      await addPurchaseInvoice(purchaseData);
      toast.success('Purchase order recorded successfully!');
      navigate('/purchases');
    } catch (error) {
      console.error('Error submitting purchase order:', error);
      toast.error('Failed to record purchase order');
    }
  };

  const removeFromOrder = (index) => {
    setCurrentOrder(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ background: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)', minHeight: '100vh', pt: 3, pb: 8 }}>
      <Container maxWidth="md">
        <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 4 }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ bgcolor: alpha("#1976d2", 0.15), p: 1.2, borderRadius: '12px', mr: 2 }}>
                  <InventoryIcon sx={{ color: "#1976d2" }} fontSize="large" />
                </Box>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  Add Purchase Order
                </Typography>
                <Chip
                  label={invoiceNumber}
                  color="primary"
                  sx={{ ml: 2, fontWeight: 'bold', fontSize: '1em', borderRadius: '8px' }}
                />
              </Box>
            }
            sx={{
              background: alpha("#1976d2", 0.08),
              borderBottom: `1px solid ${alpha("#1976d2", 0.15)}`
            }}
          />
          <CardContent>
            <Stack spacing={3}>
              {/* Supplier Selection */}
              <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#1976d2", 0.2)}` }}>
                <Stack direction="row" alignItems="center" spacing={2} mb={1}>
                  <Box sx={{ bgcolor: alpha("#1976d2", 0.15), p: 1, borderRadius: '10px' }}>
                    <PersonIcon sx={{ color: "#1976d2" }} />
                  </Box>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    Supplier
                  </Typography>
                </Stack>
                <SupplierSelect
                  value={selectedSupplier}
                  onChange={setSelectedSupplier}
                  loadOptions={loadSupplierOptions}
                />
              </Paper>

              {/* Product Selection */}
              <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#1976d2", 0.2)}` }}>
                <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                  <Box sx={{ bgcolor: alpha("#1976d2", 0.15), p: 1, borderRadius: '10px' }}>
                    <InventoryIcon sx={{ color: "#1976d2" }} />
                  </Box>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    Product
                  </Typography>
                </Stack>
                <ProductSelect
                  value={selectedProduct}
                  onChange={handleProductSelect}
                  loadOptions={async (inputValue) => {
                    const options = await loadProductOptions(inputValue);
                    return [
                      ...options,
                      { value: "new", label: "+ Add New Product" }
                    ];
                  }}
                  quantity={quantity}
                  setQuantity={setQuantity}
                  price={purchasePrice}
                  setPrice={setPurchasePrice}
                  vat={0}
                  setVat={() => {}}
                  vatOptions={[]}
                  onAdd={() => {}}
                  disabled={false}
                />
                
                {isNewProduct && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" mb={1}>Add New Product</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Product Name"
                          value={newProductData.Name}
                          onChange={e => setNewProductData({ ...newProductData, Name: e.target.value })}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="SKU"
                          value={newProductData.SKU}
                          onChange={e => setNewProductData({ ...newProductData, SKU: e.target.value })}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Sale Price"
                          type="number"
                          value={newProductData.SalePrice}
                          onChange={e => setNewProductData({ ...newProductData, SalePrice: e.target.value })}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Purchase Price"
                          type="number"
                          value={newProductData.PurchasePrice}
                          onChange={e => setNewProductData({ ...newProductData, PurchasePrice: e.target.value })}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}

                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AddIcon />}
                  fullWidth
                  size="large"
                  onClick={handleAddToOrder}
                  sx={{
                    py: 1.2,
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    mt: 2
                  }}
                  disabled={
                    !selectedSupplier ||
                    (!selectedProduct && !isNewProduct) ||
                    Number(quantity) <= 0 ||
                    Number(purchasePrice) <= 0 ||
                    (isNewProduct && (!newProductData.Name || !newProductData.SKU))
                  }
                >
                  Add to Purchase Order
                </Button>
              </Paper>

              {/* Purchase Date */}
              <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#1976d2", 0.2)}` }}>
                <TextField
                  label="Purchase Date"
                  type="date"
                  value={purchaseDate}
                  onChange={e => setPurchaseDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Paper>

              {/* Current Order Table */}
              {currentOrder.length > 0 && (
                <Paper elevation={2} sx={{ p: 2, borderRadius: '12px' }}>
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    Current Purchase Order
                  </Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f5f5f5' }}>
                          <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Product</th>
                          <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>SKU</th>
                          <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Qty</th>
                          <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Price</th>
                          <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Total</th>
                          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentOrder.map((item, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>{item.productName}</td>
                            <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>{item.sku}</td>
                            <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{item.quantity}</td>
                            <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #eee' }}>€{item.purchasePrice}</td>
                            <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #eee' }}>€{item.totalPrice.toFixed(2)}</td>
                            <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                              <Button
                                size="small"
                                color="error"
                                onClick={() => removeFromOrder(idx)}
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    size="large"
                    onClick={handleSubmitPurchaseOrder}
                    sx={{
                      mt: 2,
                      py: 1.2,
                      borderRadius: '12px',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      textTransform: 'none'
                    }}
                  >
                    Record Purchase Order
                  </Button>
                </Paper>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default AddPurchasePage;